/**
 * The register's identity as a cell: a code chip, the name, and one muted line per lineage fact.
 *
 * A stub's placeholder code reads as "code not yet assigned" rather than as a code, because
 * showing a generated string in the chip that elsewhere holds `EM` or `WP` invites it to be
 * mistaken for one.
 */

import { Link } from 'react-router';
import type { ReactNode } from 'react';
import { fmtDate, fmtYear } from '~/lib/format';
import { routes } from '~/lib/routes';
import type { JurisdictionRow, Rename, SuccessorRef } from '~/api/schema';
import styles from './Register.module.css';

const PLACEHOLDER_CODE = /^STUB\d+$/;

export function CodeChip({ code, incomplete }: { code: string; incomplete?: boolean }) {
  const unassigned = incomplete && PLACEHOLDER_CODE.test(code);
  return (
    <span className={[styles.code, unassigned ? styles.codeUnassigned : undefined].filter(Boolean).join(' ')}>
      {unassigned ? 'no code' : code}
    </span>
  );
}

export interface LineageFact {
  text: string;
  scheduled: boolean;
}

/**
 * The lineage lines, derived from values the server already computed. `scheduled` is the
 * server's flag; nothing here compares a date to today.
 */
export function lineageFacts(register: {
  renames: readonly Rename[];
  closedOn: string | null;
  closingOn: string | null;
  successors: readonly SuccessorRef[];
  status: string;
}): readonly LineageFact[] {
  const facts: LineageFact[] = [];

  for (const rename of register.renames) {
    const subject = rename.field === 'registry' ? 'Registry was' : 'Filed as';
    if (rename.scheduled) {
      facts.push({
        text:
          rename.field === 'registry'
            ? `Registry becomes ${rename.to} on ${fmtDate(rename.date)}`
            : `Becomes ${rename.to} on ${fmtDate(rename.date)}`,
        scheduled: true,
      });
    } else {
      facts.push({ text: `${subject} ${rename.from} until ${rename.until}`, scheduled: false });
    }
  }

  if (register.closingOn) {
    facts.push({ text: `Closes ${fmtDate(register.closingOn)}`, scheduled: true });
  } else if (register.closedOn) {
    facts.push({ text: `Closed ${fmtYear(register.closedOn)}`, scheduled: false });
  }

  if (register.successors.length > 0) {
    facts.push({
      text: `Rights passed to: ${register.successors.map((successor) => successor.name).join(', ')}`,
      scheduled: false,
    });
  }

  return facts;
}

export function LineageLines({ facts }: { facts: readonly LineageFact[] }) {
  if (facts.length === 0) return null;
  return (
    <div className={styles.sub}>
      {facts.map((fact) => (
        <div
          key={fact.text}
          className={[styles.line, fact.scheduled ? styles.scheduled : undefined]
            .filter(Boolean)
            .join(' ')}
        >
          {fact.text}
        </div>
      ))}
    </div>
  );
}

export interface RegisterNameCellProps {
  code: string;
  name: string;
  registry: string | null;
  incomplete?: boolean;
  inactive?: boolean;
  /** The first cell of a row is a real link to the row's route. */
  href?: string;
  facts?: readonly LineageFact[];
  badges?: ReactNode;
}

export function RegisterNameCell({
  code,
  name,
  registry,
  incomplete,
  inactive,
  href,
  facts = [],
  badges,
}: RegisterNameCellProps) {
  return (
    <div className={inactive ? styles.inactive : undefined}>
      <div className={styles.name}>
        <CodeChip code={code} incomplete={incomplete ?? false} />
        {href ? (
          <Link to={href} className={styles.nameLink}>
            {name}
          </Link>
        ) : (
          <span className={styles.nameText}>{name}</span>
        )}
        {badges}
      </div>
      <div className={styles.sub}>{registry}</div>
      <LineageLines facts={facts} />
    </div>
  );
}

export function registerHref(row: Pick<JurisdictionRow, 'code'>): string {
  return routes.overview(row.code);
}
